import React, { useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";

interface IProps {
  methods: UseFormReturn<any>;
  name: string;
  className?: string;
  label?: string;
  width?: number;
  horizontal?: number;
  placeholder?: string;
  maxLength?: number; // New prop for maximum length
  onClick?: () => void;
}

export default function Numpad({
  methods,
  name,
  className,
  label,
  width = 500,
  placeholder,
  maxLength, // Destructure maxLength
  horizontal = 2,
  onClick,
}: IProps) {
  const [showNumpad, setShowNumpad] = useState(false);
  const [inputActive, setInputActive] = useState(false);
  const numpadRef = useRef(null);

  const handleButtonClick = (value: string | number) => {
    const currentValue = methods.getValues(name);
    const newValue = currentValue + value;

    // Restrict input to maxLength if provided
    if (!maxLength || newValue.length <= maxLength) {
      methods.setValue(name, newValue);
      methods.clearErrors(name);
    }
  };

  const handleClear = () => {
    methods.setValue(name, "");
    methods.clearErrors(name);
  };

  const handleSubmit = () => {
    setShowNumpad(false);
  };

  const handleDelete = () => {
    const currentValue = methods.getValues(name);
    methods.setValue(name, currentValue.slice(0, -1));
    methods.clearErrors(name);
  };

  const handleClickOutside = (event: MouseEvent) => {
    //  @ts-ignore
    if (numpadRef.current && !numpadRef.current.contains(event.target)) {
      setShowNumpad(false);
      setInputActive(false);
    }
  };

  // Handle keyboard input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete", ".", "Enter"];
    const isNumber = !isNaN(Number(e.key));

    if (!allowedKeys.includes(e.key) && !isNumber) {
      e.preventDefault();
    }

    // Restrict keyboard input to maxLength if provided
    const currentValue = methods.getValues(name);
    if (maxLength && currentValue && currentValue.length >= maxLength && !["Backspace", "Delete"].includes(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="space-y-1" ref={numpadRef}>
      {label && <label className="text-sm dark:text-white">{label}</label>}

      <input
        type="text"
        {...methods.register(name)}
        placeholder={placeholder}
        readOnly={false}
        maxLength={maxLength} // Apply maxLength directly to the input
        onFocus={() => {
          setShowNumpad(true);
          setInputActive(true);
        }}
        onKeyDown={handleKeyDown}
        className={`${className} ${inputActive ? "border-[3px] border-blue-600 shadow-md" : ""} ${
          showNumpad ? "border-blue-500 border-6" : ""
        } focus-none outline-none`}
      />
      {showNumpad && (
        <div
          className={`w-full absolute flex items-center justify-center left-0 bottom-2 bg-white border border-gray-300 shadow-md rounded py-3 px-2 z-[100px]`}
        >
          <section className="w-full max-w-[500px] grid grid-cols-6 gap-2 z-50">
            <div className="grid grid-cols-3 gap-3 col-span-5 z-50">
              {[7, 8, 9, 4, 5, 6, 1, 2, 3, 0, ".", "Enter"].map((num) => {
                if (num === "Enter") {
                  return (
                    <button
                      id="numpad-btn-enter"
                      type="button"
                      className={enterBtn}
                      key={num}
                      onClick={() => {
                        handleSubmit();
                        if (onClick) onClick();
                      }}
                    >
                      Enter
                    </button>
                  );
                } else {
                  return (
                    <button
                      id={`numpad-btn-${num}`}
                      type="button"
                      className={numBtn}
                      key={num}
                      onClick={() => handleButtonClick(num)}
                    >
                      {num}
                    </button>
                  );
                }
              })}
            </div>

            <div className="flex flex-col justify-between space-y-5 text-white">
              <button
                id="numpad-btn-click"
                type="button"
                className="bg-[#6C89B3] hover:bg-[#40526b] py-5 rounded-md flex-center"
                onClick={() => handleDelete()}
              >
                <span>{clearIcon}</span>
              </button>

              <button
                id="numpad-btn-clear"
                type="button"
                className="bg-[#6C89B3] hover:bg-[#40526b] py-5 rounded-md font-bold text-lg"
                onClick={() => handleClear()}
              >
                AC
              </button>

              <button
                type="button"
                className="bg-[#8C8C8C] hover:bg-[#353131] py-5 flex-center rounded-md"
                onClick={() => handleSubmit()}
              >
                <span>{dropdownIcon}</span>
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

const numBtn = "bg-[#3D89F5] hover:bg-her-green text-white font-bold rounded-[5px] text-xl py-3";
const enterBtn = "bg-her-green hover:bg-[#0c3062] text-white font-bold rounded-[5px] text-xl py-3";

const clearIcon = (
  <svg width="36" height="37" viewBox="0 0 36 37" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_5406_24246" maskUnits="userSpaceOnUse" x="0" y="0" width="36" height="36">
      <rect y="0.5" width="36" height="36" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_5406_24246)">
      <path
        d="M12 29C11.5 29 11.0312 28.8875 10.5938 28.6625C10.1562 28.4375 9.8 28.125 9.525 27.725L3 18.5L9.525 9.275C9.8 8.875 10.1562 8.5625 10.5938 8.3375C11.0312 8.1125 11.5 8 12 8H27C27.825 8 28.5313 8.29375 29.1188 8.88125C29.7063 9.46875 30 10.175 30 11V26C30 26.825 29.7063 27.5313 29.1188 28.1188C28.5313 28.7063 27.825 29 27 29H12ZM12 26H27V11H12L6.675 18.5L12 26ZM15.6 24.5L19.5 20.6L23.4 24.5L25.5 22.4L21.6 18.5L25.5 14.6L23.4 12.5L19.5 16.4L15.6 12.5L13.5 14.6L17.4 18.5L13.5 22.4L15.6 24.5Z"
        fill="white"
      />
    </g>
  </svg>
);

const dropdownIcon = (
  <svg width="22" height="13" viewBox="0 0 22 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 1.5L11 10.5L20 1.5" stroke="white" strokeWidth="3.5" />
  </svg>
);
